import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Home, Users, Building2, Handshake, GitBranch, Megaphone, Workflow, Sparkles, Target, CheckSquare, Plus, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";

export const CommandBar = ({ open, setOpen, onAskAi }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`); setResults(data); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const go = (path) => { setOpen(false); setQuery(""); navigate(path); };

  const navItems = [
    { label: "Home", icon: Home, path: "/app" },
    { label: "Contacts", icon: Users, path: "/app/contacts" },
    { label: "Companies", icon: Building2, path: "/app/companies" },
    { label: "Deals", icon: Handshake, path: "/app/deals" },
    { label: "Pipeline", icon: GitBranch, path: "/app/pipeline" },
    { label: "Leads", icon: Target, path: "/app/leads" },
    { label: "Campaigns", icon: Megaphone, path: "/app/campaigns" },
    { label: "Workflows", icon: Workflow, path: "/app/workflows" },
    { label: "Tasks", icon: CheckSquare, path: "/app/tasks" },
  ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput data-testid="command-bar-input" placeholder="Search or type a command…" value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {query.length >= 2 && (
          <CommandItem onSelect={() => { setOpen(false); onAskAi(query); }} className="gap-2">
            <Sparkles className="h-4 w-4 text-[#7C3AED]" /> Ask Aurelo AI: “{query}”
          </CommandItem>
        )}
        {results && (
          <>
            {results.contacts?.length > 0 && (
              <CommandGroup heading="Contacts">
                {results.contacts.map((c) => (
                  <CommandItem key={c.id} onSelect={() => go(`/app/contacts/${c.id}`)} className="gap-2">
                    <Users className="h-4 w-4 text-slate-400" />{c.first_name} {c.last_name} <span className="text-slate-400 text-xs">· {c.company_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.companies?.length > 0 && (
              <CommandGroup heading="Companies">
                {results.companies.map((c) => (
                  <CommandItem key={c.id} onSelect={() => go(`/app/companies/${c.id}`)} className="gap-2"><Building2 className="h-4 w-4 text-slate-400" />{c.name}</CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.deals?.length > 0 && (
              <CommandGroup heading="Deals">
                {results.deals.map((d) => (
                  <CommandItem key={d.id} onSelect={() => go(`/app/deals/${d.id}`)} className="gap-2"><Handshake className="h-4 w-4 text-slate-400" />{d.name}</CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
        <CommandGroup heading="Navigate">
          {navItems.map((n) => (
            <CommandItem key={n.path} onSelect={() => go(n.path)} className="gap-2"><n.icon className="h-4 w-4 text-slate-400" />{n.label}</CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Create">
          <CommandItem onSelect={() => go("/app/contacts?new=1")} className="gap-2"><Plus className="h-4 w-4 text-slate-400" />New Contact</CommandItem>
          <CommandItem onSelect={() => go("/app/deals?new=1")} className="gap-2"><Plus className="h-4 w-4 text-slate-400" />New Deal</CommandItem>
          <CommandItem onSelect={() => go("/app/campaigns?new=1")} className="gap-2"><Plus className="h-4 w-4 text-slate-400" />New Campaign</CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
